import { useState, useEffect, useCallback } from 'react';
import { Package, Search, Filter, Loader as Loader2, ShoppingBag, Clock, CircleCheck as CheckCircle, DollarSign, MessageCircle, Ticket, Wallet, Truck, CreditCard, TriangleAlert as AlertTriangle, Printer } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { fetchOrders, getOrderStats, type OrderStats } from '@/lib/orderService';
import { getMerchantPaymentConfig } from '@/lib/merchantPayments';
import { formatOrderMoney } from '@/lib/orderPrint';
import { useCheckoutSettings } from '@/hooks/useCheckoutSettings';
import OrderStatusBadge from '@/components/orders/OrderStatusBadge';
import PaymentStatusBadge, { PAYMENT_STATUS_CONFIG } from '@/components/orders/PaymentStatusBadge';
import OrderDetailsPanel from '@/components/orders/OrderDetailsPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { generateWhatsAppUrl, cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { Order, OrderStatus, OrderPaymentStatus } from '@/types';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Todos os status' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'confirmed', label: 'Confirmados' },
  { value: 'preparing', label: 'Em preparo' },
  { value: 'shipped', label: 'Enviados' },
  { value: 'delivered', label: 'Entregues' },
  { value: 'cancelled', label: 'Cancelados' },
];

const ORDER_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Todos os tipos' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'ecommerce', label: 'Pagamento Online' },
];

const PAYMENT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Todos os pagamentos' },
  ...(['pending', 'approved', 'rejected', 'refunded', 'cancelled'] as OrderPaymentStatus[]).map((value) => ({
    value,
    label: PAYMENT_STATUS_CONFIG[value].label,
  })),
];

export default function OrdersPage() {
  const { user } = useAuth();
  const formatCurrency = (value: number) => formatOrderMoney(value, user?.currency);
  const { settings: checkoutSettings } = useCheckoutSettings();

  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<ReturnType<typeof getOrderStats> extends Promise<infer T> ? T : never | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState<string>('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [hadPaymentsEverActive, setHadPaymentsEverActive] = useState(false);
  const LIMIT = 20;

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const showPaymentUI =
    !!checkoutSettings.onlinePaymentEnabled ||
    (stats?.ecommerceCount ?? 0) > 0 ||
    hadPaymentsEverActive;

  const loadOrders = useCallback(
    async (pageNum: number, replace = false) => {
      if (!user?.id) return;
      setLoading(true);
      try {
        const filters: { status?: OrderStatus; search?: string; orderType?: 'whatsapp' | 'ecommerce'; paymentStatus?: OrderPaymentStatus } = {};
        if (statusFilter !== 'all') filters.status = statusFilter as OrderStatus;
        if (searchQuery.trim()) filters.search = searchQuery.trim();
        if (orderTypeFilter !== 'all') filters.orderType = orderTypeFilter as 'whatsapp' | 'ecommerce';
        if (paymentStatusFilter !== 'all') filters.paymentStatus = paymentStatusFilter as OrderPaymentStatus;

        const { data, count } = await fetchOrders(user.id, LIMIT, pageNum * LIMIT, filters);
        setOrders((prev) => (replace ? data : [...prev, ...data]));
        setTotalCount(count);
        setHasMore((pageNum + 1) * LIMIT < count);
      } catch (err) {
        console.error('Failed to load orders:', err);
      } finally {
        setLoading(false);
      }
    },
    [user?.id, statusFilter, searchQuery, orderTypeFilter, paymentStatusFilter]
  );

  const loadStats = useCallback(async () => {
    if (!user?.id) return;
    const s = await getOrderStats(user.id);
    setStats(s);
  }, [user?.id]);

  useEffect(() => {
    setPage(0);
    loadOrders(0, true);
  }, [loadOrders]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (!user?.id) return;
    getMerchantPaymentConfig()
      .then((res) => setHadPaymentsEverActive(!!res?.config))
      .catch(() => setHadPaymentsEverActive(false));
  }, [user?.id]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    loadOrders(next);
  };

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const handleStatusUpdate = (orderId: string, newStatus: OrderStatus) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
    );
    if (selectedOrder?.id === orderId) {
      setSelectedOrder((prev) => (prev ? { ...prev, status: newStatus } : null));
    }
    loadStats();
  };

  const handleTrackingUpdate = (orderId: string, tracking: { carrier: string | null; tracking_code: string | null }) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, ...tracking } : o))
    );
    if (selectedOrder?.id === orderId) {
      setSelectedOrder((prev) => (prev ? { ...prev, ...tracking } : null));
    }
  };

  const handlePaymentStatusUpdate = (orderId: string, paymentStatus: OrderPaymentStatus) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, payment_status: paymentStatus } : o))
    );
    if (selectedOrder?.id === orderId) {
      setSelectedOrder((prev) => (prev ? { ...prev, payment_status: paymentStatus } : null));
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-6xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl page-title">Pedidos</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {showPaymentUI
              ? 'Gerencie os pedidos recebidos via WhatsApp e pagamento online'
              : 'Gerencie os pedidos recebidos via WhatsApp'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 self-start sm:self-auto shrink-0"
          disabled={totalCount === 0}
          onClick={() => {
            const params = new URLSearchParams({ print: '1' });
            if (statusFilter !== 'all') params.set('status', statusFilter);
            if (orderTypeFilter !== 'all') params.set('orderType', orderTypeFilter);
            if (paymentStatusFilter !== 'all') params.set('paymentStatus', paymentStatusFilter);
            if (searchQuery.trim()) params.set('search', searchQuery.trim());
            window.open(`/dashboard/orders/imprimir-lista?${params.toString()}`, '_blank');
          }}
        >
          <Printer className="h-3.5 w-3.5" />
          Imprimir lista
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className={cn('grid grid-cols-2 gap-3 md:gap-4', showPaymentUI ? 'md:grid-cols-5' : 'md:grid-cols-4')}>
          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <ShoppingBag className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total (30 dias)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Clock className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">Pendentes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.delivered}</p>
                  <p className="text-xs text-muted-foreground">Entregues</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {showPaymentUI && (
            <Card>
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <CreditCard className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.awaitingPayment}</p>
                    <p className="text-xs text-muted-foreground">Aguardando pagamento</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <DollarSign className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
                  <p className="text-xs text-muted-foreground">Receita (30 dias)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome do cliente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-[200px]">
            <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {showPaymentUI && (
          <>
            <Select value={orderTypeFilter} onValueChange={setOrderTypeFilter}>
              <SelectTrigger className="sm:w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORDER_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}>
              <SelectTrigger className="sm:w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </div>

      {/* Orders List */}
      {loading && orders.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : orders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
              <ShoppingBag className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="font-medium text-muted-foreground">Nenhum pedido encontrado</p>
            <p className="text-sm text-muted-foreground/70 mt-1 max-w-xs">
              {statusFilter !== 'all' || orderTypeFilter !== 'all' || paymentStatusFilter !== 'all' || searchQuery
                ? 'Tente alterar os filtros para ver mais pedidos.'
                : 'Os pedidos feitos pelos seus clientes aparecerão aqui.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {orders.map((order) => {
            const itemCount = order.order_items?.length || 0;
            const whatsappUrl = order.customer_whatsapp
              ? generateWhatsAppUrl(
                  order.customer_whatsapp,
                  '',
                  order.customer_country_code || '55'
                )
              : '';

            return (
              <div
                key={order.id}
                className="border rounded-lg p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                onClick={() => handleOrderClick(order)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Package className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{order.customer_name}</p>
                        <OrderStatusBadge status={order.status} />
                        {order.order_type === 'ecommerce' && (
                          <PaymentStatusBadge status={order.payment_status} />
                        )}
                        <Badge variant="outline" className="text-xs">
                          {order.source === 'cart' ? 'Carrinho' : 'Produto'}
                        </Badge>
                        {order.coupon_code && (
                          <Badge variant="outline" className="text-xs">
                            <Ticket className="h-3 w-3 mr-1" />
                            {order.coupon_code}
                          </Badge>
                        )}
                        {order.stock_shortfall && (
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Estoque pendente
                          </Badge>
                        )}
                        {order.payment_method && (
                          <Badge variant="outline" className="text-xs">
                            <Wallet className="h-3 w-3 mr-1" />
                            {order.payment_method}
                          </Badge>
                        )}
                        {order.delivery_option && (
                          <Badge variant="outline" className="text-xs">
                            <Truck className="h-3 w-3 mr-1" />
                            {order.delivery_option}
                            {order.delivery_is_quote && ' (A combinar)'}
                            {!order.delivery_is_quote && order.delivery_scope === 'local' && ' (Local)'}
                            {!order.delivery_is_quote && order.delivery_scope === 'national' && ' (Nacional)'}
                            {!order.delivery_is_quote && order.delivery_scope === 'pickup' && ' (Retirada)'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span>{itemCount} {itemCount === 1 ? 'item' : 'itens'}</span>
                        <span>{format(new Date(order.created_at), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 sm:flex-shrink-0">
                    <span className="text-lg font-bold text-primary">
                      {formatCurrency(order.total)}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Imprimir pedido"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/dashboard/orders/${order.id}/imprimir?print=1`, '_blank');
                      }}
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                    {whatsappUrl && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(whatsappUrl, '_blank');
                        }}
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {hasMore && (
            <div className="flex justify-center pt-4">
              <Button variant="outline" onClick={handleLoadMore} disabled={loading}>
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Carregar mais
              </Button>
            </div>
          )}

          <p className="text-xs text-center text-muted-foreground pt-2">
            Mostrando {orders.length} de {totalCount} pedidos
          </p>
        </div>
      )}

      <OrderDetailsPanel
        order={selectedOrder}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        onStatusUpdate={handleStatusUpdate}
        onTrackingUpdate={handleTrackingUpdate}
        onPaymentStatusUpdate={handlePaymentStatusUpdate}
      />
    </div>
  );
}
